import React from "react";
import { debounce } from "lodash";
import moment from "moment";
import DatePicker from "react-datepicker";
import { withTranslation } from "react-i18next";
import { Checkbox, Dropdown, Form, Icon, Label, Message, Popup } from "semantic-ui-react";

import { BrandGroupDropdown, UserInput } from "../../components";
import { validateEmail, isAdmin } from "../../util/common";
import { ROLES } from "./roles";
import Service from "./service";

const ALL_BRANDS = 0;
const ACCEPTED_PASSWORD_STRENGTH = 4;
class UserForm extends React.Component {
  constructor(props) {
    super(props);

    const selectedDealer = this.props.dealers?.find(dealer => dealer.id === props.user.dealer_id);

    this.state = {
      extraDealers: props.user.dealers_ids?.length ? props.user.dealers_ids : [],
      allowedLocations: props.user.location_ids?.length ? props.user.location_ids : [],
      brandGroups: props.user.brand_groups?.length ? props.user.brand_groups.map(brandGroup => brandGroup.id) : [ALL_BRANDS],
      showDealers: false,
      showLocations: false,
      passwordVisibility: [false, false],
      oneiPlanningEnabled: selectedDealer?.onei_planning_enabled,
      planitPlanningEnabled: selectedDealer?.planit_planning_enabled,
      errorMessage: "",
    };
  }

  componentDidMount() {
    this.renderBasedOnSelectedRole(this.props.user.role_id);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user.dealer_id !== this.props.user.dealer_id) {
      const dealer = this.props.dealers?.find(dealer => dealer.id === this.props.user.dealer_id);
      if (dealer) {
        this.setState({ oneiPlanningEnabled: dealer.onei_planning_enabled, planitPlanningEnabled: dealer.planit_planning_enabled });
      }
    }
  }

  handleInputChange = e => {
    const { user } = this.props;
    const shouldCallGetPasswordStrenghtOnChange = ["first_name", "last_name", "username", "email"];

    if (Object.keys(user).length === 0) return;
    user[e.target.name] = e.target.value;
    this.props.handleUserChange(user);

    if (shouldCallGetPasswordStrenghtOnChange.includes(e.target.name)) this.handleGetPasswordStrengthDebounced();
  };

  handleCheckboxChange = (e, data) => {
    let { user } = this.props;
    user[data.name] = data.checked;
    this.props.handleUserChange(user);
  };

  handleDateChange = e => {
    const { user } = this.props;
    user.birthday = moment(e).format();
    this.props.handleUserChange(user);
  };

  handleRoleChange = (e, data) => {
    const { user } = this.props;
    user.role_id = data.value;

    this.renderBasedOnSelectedRole(data.value);
    this.props.handleRoleChange(user);
  };

  handleGetPasswordStrength = () => {
    const { password, user } = this.props;
    const { first_name, last_name, username, email } = user;

    if (!password.newPassword) return;

    Service.getUserPasswordStrength({ password: password.newPassword, first_name, last_name, username, email })
      .then(res => {
        this.props.onPasswordChange({ ...password, strength: res?.data?.data?.strength ?? 0 });
      })
      .catch(err => console.log("Error", err));
  };

  handleGetPasswordStrengthDebounced = debounce(this.handleGetPasswordStrength, 500);

  handlePasswordChange = (event, data) => {
    const { password } = this.props;
    password[data.name] = data.value;
    this.props.onPasswordChange(password);

    if (data.name === "newPassword") {
      this.handleGetPasswordStrengthDebounced();
    }
  };

  handleBrandGroupsChange = brandGroupIDs => {
    const { user } = this.props;

    this.setState({ brandGroups: brandGroupIDs }, () => {
      const { brandGroups } = this.state;
      user.brand_group_ids = brandGroups.includes(ALL_BRANDS) ? [] : brandGroups;
      this.props.handleUserChange({ ...user });
    });
  };

  handleDealersChange = (e, data) => {
    this.setState({ extraDealers: data.value }, () => {
      const { user, dealers } = this.props;
      user.dealers_ids = this.state.extraDealers;
      if (user.location_ids?.length) {
        const locations = [];
        const dealer = dealers.find(d => d.id === user.dealer_id);
        if (dealer && dealer.locations?.length) locations.push(...dealer.locations);

        if (user.dealers_ids?.length) {
          user.dealers_ids.forEach(dealer_id => {
            const dealer = dealers.find(d => d.id === dealer_id);
            if (dealer && dealer.locations?.length) locations.push(...dealer.locations);
          });
        }

        user.location_ids = user.location_ids.filter(location_id => locations.some(l => l.id === location_id));
        this.setState({ allowedLocations: user.location_ids });
      }

      this.props.handleUserChange({ ...user });
    });
  };

  handleLocationsChange = (e, data) => {
    this.setState({ allowedLocations: data.value }, () => this.props.handleAllowedLocationsChange(this.state.allowedLocations));
  };

  togglePassword = i => {
    let passwordVisibility = this.state.passwordVisibility;
    passwordVisibility[i] = !passwordVisibility[i];
    this.setState({ passwordVisibility });
  };

  renderBasedOnSelectedRole = role => {
    this.setState({
      showDealers: [ROLES.CONSULTANT, ROLES.SUPERVISOR, ROLES.WARRANTY_MANAGER, ROLES.MANUFACTURER].includes(role),
      showLocations: role === ROLES.WARRANTY_MANAGER,
    });
  };

  renderExtraDealers = canEditAll => {
    const { t } = this.props;

    const dealers = this.props.dealers
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      })
      .filter(d => d.id !== this.props.user.dealer_id);

    return (
      <Form.Field>
        <label>{t("extra_dealers").message || "Extra dealers"}</label>
        <Dropdown
          placeholder={t("extra_dealers").message || "Extra dealers"}
          options={
            dealers.map((a, key) => {
              return { text: a.name, value: a.id, key, description: a.active ? "" : t("inactive").message || "Inactive" };
            }) || []
          }
          selection
          search
          defaultValue={this.state.extraDealers}
          onChange={this.handleDealersChange}
          multiple
          upward={false}
          disabled={!canEditAll}
        />
      </Form.Field>
    );
  };

  renderAllowedLocations = canEditAll => {
    const { t, user, invalidInput } = this.props;
    const { allowedLocations } = this.state;

    const allSelectedDealers = [],
      dealerLocations = [],
      dealers = [];

    user.dealer_id && allSelectedDealers.push(user.dealer_id);
    this.state.extraDealers && allSelectedDealers.push(...this.state.extraDealers);

    if (allSelectedDealers.length) {
      allSelectedDealers.forEach(d => {
        const dealer = this.props.dealers.find(l => l.id === d);

        if (dealer?.locations) dealers[dealer.id] = { name: dealer.name, locations: dealer.locations };
      });

      dealers.sort((a, b) => a.name.localeCompare(b.name));
      dealers.forEach(d => {
        d.locations.sort((a, b) => a.name.localeCompare(b.name));
      });
    }

    if (dealers) {
      dealers.forEach((d, i) => {
        dealerLocations.push({
          key: `${d.name}-${i}`,
          disabled: true,
          children: (
            <Dropdown.Menu scrolling>
              <Dropdown.Header content={d.name} />
              <Dropdown.Divider />
            </Dropdown.Menu>
          ),
        });

        d.locations.forEach(l => {
          dealerLocations.push({
            key: l.id,
            text: l.name,
            value: l.id,
            fulltext: `${d.name.substring(0, 3)} - ${l.name}`,
            description: l.active ? "" : t("inactive").message || "Inactive",
          });
        });
      });
    }

    return (
      <Form.Field>
        <label>{t("allowed_locations").message || "Allowed locations"}</label>
        <Dropdown
          placeholder={t("allowed_location").message || "Allowed locations"}
          options={dealerLocations}
          multiple
          value={allowedLocations}
          onChange={this.handleLocationsChange}
          selection
          upward={false}
          disabled={!canEditAll}
          renderLabel={label => ({
            content: label.fulltext,
          })}
        />
        {invalidInput && !allowedLocations.length && (
          <Label basic color="red" pointing>
            {t("field_is_required").message || "This field is required."}
          </Label>
        )}
      </Form.Field>
    );
  };

  renderBrandGroups = canEditAll => {
    const { t, invalidInput } = this.props;
    const { brandGroups } = this.state;

    return (
      <Form.Field>
        <label>{t("brand_groups").message || "Brand Groups"}</label>
        <BrandGroupDropdown value={brandGroups} onChange={this.handleBrandGroupsChange} showAllBrands disabled={!canEditAll} />
        {invalidInput && !brandGroups.length && (
          <Label basic color="red" pointing>
            {t("field_is_required").message || "This field is required."}
          </Label>
        )}
      </Form.Field>
    );
  };

  renderPassCheckMarkIcon = type => {
    const { t, password } = this.props;
    let r = null;
    let c = "";
    let goodStyle = {
      fontSize: "1.15em",
      position: "absolute",
      marginTop: "8px",
      color: "green",
    };

    let badStyle = {
      fontSize: "1.15em",
      position: "absolute",
      marginTop: "8px",
      color: "#f54545",
    };

    if (type === "new" && password.newPassword && password.newPassword.length > 0) {
      if (password.strength >= ACCEPTED_PASSWORD_STRENGTH) {
        r = <Icon name="check circle outline" style={goodStyle} />;
        c = t("new_password_secure").message || "New password looks good";
      } else {
        r = <Icon name="times circle outline" style={badStyle} />;
        c = t("new_password_not_secure").message || "New password is not secure enough";
      }
    }

    if (type === "confirm" && password.passwordConfirmation && password.passwordConfirmation.length > 0) {
      if (password.strength >= ACCEPTED_PASSWORD_STRENGTH && password.newPassword === password.passwordConfirmation) {
        r = <Icon name="check circle outline" style={goodStyle} />;

        c = t("new_confirm_password_secure").message || "New password confirmation looks good";
      } else {
        r = <Icon name="times circle outline" style={badStyle} />;
        c = t("new_confirm_password_not_secure").message || "Confirmation is not the same as password or not secure enough";
      }
    }

    return <Popup trigger={r} content={c} />;
  };

  appointmentListOptions = () => [
    {
      key: "time_car_app",
      text: this.props.t("appointment_date").message || "Appointment date",
      value: false,
    },
    {
      key: "scheduled_in_out",
      text: this.props.t("scheduled_in_out").message || "Scheduled in/out date",
      value: true,
    },
  ];

  isAITranslationSettingVisible = () => {
    const { locationsBySelectedDealer, user, mode } = this.props;

    if (user.role_id !== ROLES.MECHANIC || mode !== "edit") return false;

    return locationsBySelectedDealer.some(location => location.id === user.dealer_location_id && location.is_tablet_translation_ai_enabled);
  };

  render() {
    const {
      user,
      roleOptions,
      dealers,
      dmsUsers,
      loadingDMSUsers,
      t,
      locationsBySelectedDealer,
      loadingPlanningMechanicsLists,
      oneiPlanningMechanicsOptions,
      planItMechanicsOptions,
      handleUserChange,
      password,
      mode,
      currentUser,
      invalidInput,
      errorMessage,
    } = this.props;
    const { passwordVisibility, oneiPlanningEnabled, planitPlanningEnabled } = this.state;

    const canEditAll = mode === "create" || isAdmin(currentUser?.role_id);

    return (
      user && (
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("role").message || "Role"}</label>
              <Dropdown
                placeholder={t("role").message || "Role"}
                options={roleOptions.filter(r => r.value !== -99)}
                selection
                value={user.role_id}
                onChange={this.handleRoleChange}
                disabled={!canEditAll}
              />
              {invalidInput && user.role_id === null && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
            {!this.props.isDealersHidden && (
              <Form.Field>
                <label>{t("default_dealer").message || "Default Dealer"}</label>
                <Dropdown
                  placeholder={t("default_dealer").message || "Default Dealer"}
                  options={
                    dealers.map((a, key) => {
                      return { text: a.name, value: a.id, key, description: a.active ? "" : t("inactive").message || "Inactive" };
                    }) || []
                  }
                  selection
                  search
                  value={user.dealer_id}
                  onChange={this.props.handleDealerChange}
                  disabled={!canEditAll}
                />
                {invalidInput && !user.dealer_id && (
                  <Label basic color="red" pointing>
                    {t("field_is_required").message || "This field is required."}
                  </Label>
                )}
              </Form.Field>
            )}
            <Form.Field>
              <label>{t("default_location").message || "Default Location"}</label>
              <Dropdown
                placeholder={t("default_location").message || "Default Location"}
                options={locationsBySelectedDealer.map((a, key) => ({
                  text: a.name,
                  value: a.id,
                  key,
                  description: a.active ? "" : t("inactive").message || "Inactive",
                }))}
                search
                selection
                noResultsMessage={
                  user.role_id === ROLES.WARRANTY_MANAGER
                    ? t("assign_allowed_locations_message").message || "Please assign allowed locations first."
                    : t("no_locations_found").message || "No locations found on selected dealer."
                }
                value={user.dealer_location_id}
                onChange={this.props.handleLocationChange}
                disabled={!canEditAll}
              />
              {invalidInput && !user.dealer_location_id && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
          </Form.Group>

          <Form.Group widths={"equal"}>
            {this.renderBrandGroups(canEditAll)}
            {this.state.showDealers ? this.renderExtraDealers(canEditAll) : <Form.Field></Form.Field>}
            {this.state.showLocations ? this.renderAllowedLocations(canEditAll) : <Form.Field></Form.Field>}
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("username").message || "Username"}</label>
              <UserInput value={user.username} name="username" onChange={this.handleInputChange} disabled={!canEditAll} />
              {invalidInput && !user.username && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
            <Form.Field>
              <label>{t("first_name").message || "Firstname"}</label>
              <UserInput value={user.first_name} name="first_name" onChange={this.handleInputChange} disabled={!canEditAll} />
              {invalidInput && !user.first_name && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
            <Form.Field>
              <label>{t("last_name").message || "Lastname"}</label>
              <UserInput value={user.last_name} name="last_name" onChange={this.handleInputChange} disabled={!canEditAll} />
              {invalidInput && !user.last_name && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("email").message || "Email"}</label>
              <UserInput value={user.email} name="email" onChange={this.handleInputChange} disabled={!canEditAll} />
              {invalidInput && !user.email && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}

              {invalidInput && user.email && !validateEmail(user.email) && (
                <Label basic color="red" pointing>
                  {t("email_not_valid").message || "Email is not valid"}
                </Label>
              )}
            </Form.Field>

            <Form.Field required>
              <label>{this.props.t("new_password").message || "New password"}</label>
              <UserInput
                name="newPassword"
                style={{ width: "90%" }}
                value={password.newPassword || ""}
                onChange={this.handlePasswordChange}
                type={passwordVisibility[0] ? "text" : "password"}
                icon={<Icon name={passwordVisibility[0] ? "hide" : "unhide"} link onClick={() => this.togglePassword(0)} />}
                placeholder={this.props.t("new_password").message || "New password"}
              />
              {this.renderPassCheckMarkIcon("new")}
              {password.newPassword.length > 0 && (
                <div className="strength-meter">
                  <div className="strength-meter-fill" data-strength={password.strength}></div>
                </div>
              )}
              {invalidInput && mode === "create" && !password["newPassword"] && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
            <Form.Field required>
              <label>{this.props.t("confirm_new_password").message || "Confirm new password"}</label>
              <UserInput
                name="passwordConfirmation"
                style={{ width: "90%" }}
                value={password.passwordConfirmation || ""}
                onChange={this.handlePasswordChange}
                type={passwordVisibility[2] ? "text" : "password"}
                icon={<Icon name={passwordVisibility[2] ? "hide" : "unhide"} link onClick={() => this.togglePassword(2)}></Icon>}
                placeholder={this.props.t("confirm_new_password").message || "Confirm new password"}
              />
              {this.renderPassCheckMarkIcon("confirm")}
              {invalidInput && mode === "create" && !password["passwordConfirmation"] && (
                <Label basic color="red" pointing>
                  {t("field_is_required").message || "This field is required."}
                </Label>
              )}
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("address_1").message || "Address 1"}</label>
              <UserInput value={user.address1} name="address1" placeholder={t("address").message || "Address"} onChange={this.handleInputChange} disabled={!canEditAll} />
            </Form.Field>

            <Form.Field>
              <label>{t("address_2").message || "Address 2"}</label>
              <UserInput value={user.address2} name="address2" placeholder={t("address").message || "Address"} onChange={this.handleInputChange} disabled={!canEditAll} />
            </Form.Field>

            <Form.Field>
              <label>{t("phone").message || "Phone"}</label>
              <UserInput value={user.phone} name="phone" placeholder={t("phone").message || "Phone"} onChange={this.handleInputChange} />
            </Form.Field>
            <Form.Field>
              <label>{t("mobile").message || "Mobile"}</label>
              <UserInput value={user.mobile} name="mobile" placeholder={t("mobile").message || "Mobile"} onChange={this.handleInputChange} disabled={!canEditAll} />
            </Form.Field>
            <Form.Field>
              <label>{t("birthday_date").message || "Birthday"}</label>
              <DatePicker
                dateFormat="dd-MM-yyyy"
                placeholderText={t("birthday_date").message || "Birthday date"}
                name="birthday"
                selected={user?.birthday && moment(user.birthday).toDate()}
                onChange={this.handleDateChange}
                onChangeRaw={e => e.preventDefault()}
                showMonthDropdown
                showYearDropdown
                disabled={!canEditAll}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("dms_location_source").message || "DMS Location Source"}</label>
              <UserInput
                value={user.dms_location_source}
                name="dms_location_source"
                placeholder={t("dms_location_source").message || "DMS Location Source"}
                onChange={this.handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <label>{t("external_user_id").message || "External User ID"}</label>
              <UserInput
                value={user.external_user_id}
                name="external_user_id"
                placeholder={t("external_user_id").message || "External User ID"}
                onChange={this.handleInputChange}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("multi_device_login").message || "Multi-device login"}</label>
              <Checkbox
                checked={user.multi_device_login_enabled}
                name="multi_device_login_enabled"
                label={t("multi_device_login").message || "Multi-device login"}
                onChange={this.handleCheckboxChange}
                disabled={!canEditAll}
              />
            </Form.Field>
            <Form.Field>
              <label>{t("can_download_csv").message || "Can download CSV files"}</label>
              <Checkbox
                checked={user.csv_download}
                name="csv_download"
                label={t("allowed").message || "Allowed"}
                onChange={this.handleCheckboxChange}
                disabled={!canEditAll}
              />
            </Form.Field>
            <Form.Field>
              <label>{t("keylocker_allowed").message || "Keylocker allowed"}</label>
              <Checkbox
                checked={user.is_keylocker_allowed}
                name="is_keylocker_allowed"
                label={t("allowed").message || "Allowed"}
                onChange={this.handleCheckboxChange}
                disabled={!canEditAll}
              />
            </Form.Field>
            {this.isAITranslationSettingVisible() && (
              <Form.Field>
                <label>{t("translation_ai_on_tablet").message || "Translation AI on tablet"}</label>
                <Checkbox
                  checked={user.is_tablet_translation_ai_enabled}
                  toggle
                  name="is_tablet_translation_ai_enabled"
                  onChange={this.handleCheckboxChange}
                  disabled={!canEditAll}
                />
              </Form.Field>
            )}

            <Form.Field>
              <label>{this.props.t("use_counter_tablets").message || "Use counter tablets"}</label>
              <Checkbox
                checked={user.is_counter_tablet_user}
                toggle
                disabled={!canEditAll}
                onChange={(e, data) => {
                  user.is_counter_tablet_user = data.checked;
                  handleUserChange({ ...user });
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>{this.props.t("enable_tyre_ordering").message || "Enable tyre ordering"}</label>
              <Checkbox checked={user.tyre_ordering_enabled} name="tyre_ordering_enabled" toggle onChange={this.handleCheckboxChange} disabled={!canEditAll} />
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.Field>
              <label>{this.props.t("list_appointments_by").message || "List appointments by"}</label>
              <Dropdown
                name="list_appointments_by_scheduled_range"
                className="icon"
                value={!!user.list_appointments_by_scheduled_range}
                button
                floating
                icon="list"
                labeled
                disabled={!canEditAll}
                onChange={(event, data) => {
                  user.list_appointments_by_scheduled_range = data.value;
                  handleUserChange({ ...user });
                }}
                options={this.appointmentListOptions()}
              />
            </Form.Field>

            {dmsUsers.length > 0 && (
              <Form.Field width={6}>
                <label>{t("dms_user").message || "DMS User"}</label>
                <Dropdown
                  fluid
                  search
                  clearable
                  selection
                  selectOnBlur={false}
                  placeholder={t("dms_user").message || "DMS User"}
                  name="dms_user"
                  loading={loadingDMSUsers}
                  value={user.dms_nr}
                  onChange={(_e, data) => handleUserChange({ ...user, dms_nr: data.value })}
                  options={dmsUsers}
                  disabled={!canEditAll}
                />
              </Form.Field>
            )}

            {[ROLES.MECHANIC, ROLES.CHIEF_MECHANIC].includes(user.role_id) && oneiPlanningEnabled && (
              <Form.Field width={5}>
                <label>{t("onei_planning_monteur").message || "1i Planning Monteur"}</label>
                <Dropdown
                  fluid
                  search
                  clearable
                  selection
                  placeholder={t("onei_planning_monteur").message || "1i Planning Monteur"}
                  name="onei_planning_monteur_id"
                  loading={loadingPlanningMechanicsLists}
                  value={user.onei_planning_monteur_id}
                  onChange={(_e, data) => handleUserChange({ ...user, onei_planning_monteur_id: data.value })}
                  options={oneiPlanningMechanicsOptions}
                  disabled={!canEditAll}
                />
              </Form.Field>
            )}

            {[ROLES.MECHANIC, ROLES.CHIEF_MECHANIC].includes(user.role_id) && planitPlanningEnabled && (
              <Form.Field width={5}>
                <label>{t("planit_mechanic").message || "Plan IT Mechanic"}</label>
                <Dropdown
                  fluid
                  search
                  clearable
                  selection
                  selectOnBlur={false}
                  placeholder={t("planit_mechanic").message || "Plan IT Mechanic"}
                  name="planit_planning_mechanic_id"
                  loading={loadingPlanningMechanicsLists}
                  value={user.planit_planning_mechanic_id}
                  onChange={(_e, data) => handleUserChange({ ...user, planit_planning_mechanic_id: data.value })}
                  options={planItMechanicsOptions}
                  disabled={!canEditAll}
                />
              </Form.Field>
            )}
          </Form.Group>

          {errorMessage && (
            <Form.Group>
              <Form.Field width={16}>
                <Message color="red">{errorMessage}</Message>
              </Form.Field>
            </Form.Group>
          )}
        </Form>
      )
    );
  }
}

export default withTranslation()(UserForm);
